import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppRouteReuseStrategy } from "@app-shared/strategies/app-route-reuse.strategy";
import { MarketPlacePersonalizationUtils } from "@app-components/layouts/shopkeeper-layout/marketplace-personalization";
import { PageControl } from "@app-model/page-control";
import { CastOffVehiclesSummary, OemDealerService } from "@app-services/oem-dealer.service";
import { OpenService, VehicleFilter } from "@app-services/open.service";
import { SessionService } from "@app/store/session/session.service";
import { Permission, Role, UserInfo } from "@app/store/session/session.store";
import * as moment from "moment";

@Component({
  selector: "app-cast-off-vehicles-summary",
  templateUrl: "./cast-off-vehicles-summary.component.html",
  styleUrls: ["./cast-off-vehicles-summary.component.scss"],
})
export class CastOffVehiclesSummaryComponent implements OnInit {

  loading: boolean;
  summary: CastOffVehiclesSummary;

  user: UserInfo;

  @Input() filter: VehicleFilter;
  colorCoolGrey = "#a6aaaf";

  constructor(
    private sessionService: SessionService,
    private openService: OpenService,
    private oemDealerService: OemDealerService,
    private router: Router,
    private route: ActivatedRoute,
    private routeReuse: AppRouteReuseStrategy) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.filter) {
      this.submitFilter();
    }
  }

  ngOnInit() {
    this.openService.getLayoutVars().subscribe(layoutVars => {
      if (layoutVars?.menuItemColor && MarketPlacePersonalizationUtils.isWhite(layoutVars.menuItemColor)) {
        this.colorCoolGrey = layoutVars.offerColor;
      }
    });

    this.sessionService.getUser()
      .subscribe(u => {
        this.user = u;

        this.submitFilter();
      });
  }

  get unpublishedTotal(): number {
    return (this.summary?.neverPublished || 0) + (this.summary?.inactivated || 0);
  }

  get offersTotal(): number {
    return (this.summary?.withOffer || 0)
      + (this.summary?.withPersonalizedOffer || 0)
      + (this.summary?.withConditionalOffer || 0);
  }

  get showUnpublishedCard(): boolean {
    return this.unpublishedTotal > 0;
  }

  get showPublishedCard(): boolean {
    return (this.summary?.published || 0) > 0
      || (this.summary?.fastQuote || 0) > 0
      || (this.summary?.integratedSuperbid || 0) > 0
      || (this.summary?.republishedMultipleTimes || 0) > 0;
  }

  get showOffersCard(): boolean {
    return this.offersTotal > 0;
  }

  get showSoldCard(): boolean {
    return (this.summary?.soldThisMonth || 0) > 0
      || (this.summary?.soldToday || 0) > 0
      || (this.summary?.soldWithPendingPayment || 0) > 0;
  }

  routeToUnpublished(): void {
    this.refreshFilter(['NO_AUCTION', 'INACTIVE']);
  }

  routeToNeverPublished(event: MouseEvent): void {
    this.applyCardFilter(event, ['NO_AUCTION']);
  }

  routeToInactivated(event: MouseEvent): void {
    this.applyCardFilter(event, ['INACTIVE']);
  }

  routeToPublished(): void {
    this.refreshFilter(['WAITING_FIRST_BID', 'WITH_BIDS', 'FAST_QUOTE']);
  }

  routeToFastQuote(event: MouseEvent): void {
    this.applyCardFilter(event, ['FAST_QUOTE']);
  }

  routeToIntegratedSuperbid(event: MouseEvent): void {
    this.applyCardFilter(event, ['INTEGRATED_SUPERBID']);
  }

  routeToRepublishedMultipleTimes(event: MouseEvent): void {
    this.applyCardFilter(event, ['REPUBLISHED_MULTIPLE_TIMES']);
  }

  routeToOffers(): void {
    this.refreshFilter(['WITH_BIDS', 'PENDING_ALTERNATIVE_BID', 'PENDING_CONDITIONAL_OFFER']);
  }

  routeToWaitingClosing(event: MouseEvent): void {
    this.applyCardFilter(event, ['WITH_BIDS']);
  }

  routeToWithPersonalizedOffer(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(["/vender/administracao-ofertas"], { queryParams: { type: "ALTERNATIVE_BID" } });
  }

  routeToWithConditionalOffer(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(["/vender/administracao-ofertas"], { queryParams: { type: "CONDITIONAL_OFFER" } });
  }

  routeToSold(): void {
    this.routeToAuctionReport(
      moment().startOf('month').startOf('day'),
      moment().startOf('day'),
      'statuses',
      ['SOLD']
    );
  }

  routeToSoldTodayReport(event: MouseEvent): void {
    event.stopPropagation();
    const today = moment().startOf('day');
    this.routeToAuctionReport(today, today, 'statuses', ['SOLD']);
  }

  routeToSoldWithPendingPayment(event: MouseEvent): void {
    event.stopPropagation();
    this.routeToAuctionReport(
      moment().startOf('month').startOf('day'),
      moment().startOf('day'),
      'expandedStatus',
      ['SOLD_NO_PAYMENT']
    );
  }

  private submitFilter(): void {

    if (this.user && (this.user.hasPermissionDeskSell() || this.user.hasRole(Role.LOCATION_MANAGER)
      || this.user.hasAnyPermissionOrLegacy(Permission.VIEW_CAST_OFF_VEHICLES_SUMMARY))) {
      //load summary if user has access
      this.loading = true;
      this.oemDealerService.getVehiclesSummary(this.filter)
        .subscribe(s => {
          this.summary = s;
          this.loading = false;
        });
    }
  }

  private refreshFilter(expandedStatuses?: string[], sortField?: string, sortDirection?: 'ASC' | 'DESC'): void {
    const filter = this.filter;
    filter.pageControl = new PageControl();
    filter.pageControl.page = 0;
    filter.pageControl.sortField = sortField;
    filter.pageControl.sortDirection = sortDirection;
    filter.expandedStatuses = expandedStatuses;

    // Recria a listagem para reexecutar a busca com o novo filtro.
    this.routeReuse.forceReloadOnce();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: filter.toParamMap(),
      replaceUrl: true
    });
  }

  private applyCardFilter(event: MouseEvent, expandedStatuses: string[]): void {
    event.stopPropagation();
    this.refreshFilter(expandedStatuses);
  }

  private routeToAuctionReport(
    start: moment.Moment,
    end: moment.Moment,
    statusParam: 'statuses' | 'expandedStatus',
    statusValues: string[]
  ): void {
    this.router.navigate(['/vender/relatorios'], {
      queryParams: {
        soldDateIni: start.toISOString(),
        soldDateEnd: end.toISOString(),
        [statusParam]: JSON.stringify(statusValues)
      }
    });
  }

}
